// frontend/src/hooks/useSubscription.js
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionAPI } from '../services/subscriptionAPI';
import { useAuth } from '../components/auth';
import logger from '../utils/logger';

// Subscription tier hierarchy for comparison
const TIER_HIERARCHY = {
  'free': 0,
  'basic': 1,
  'professional': 2,
  'shop_pro': 3, // Legacy
  'enterprise': 4 // Legacy
};

// Feature access mapping
const FEATURE_ACCESS = {
  // Vehicle Management - Free tier limited to 1 vehicle
  'vehicle_profiles': ['free', 'basic', 'professional', 'mechanic_essential', 'mechanic_pro', 'shop_pro', 'enterprise'],
  'vehicle_repairs': ['basic', 'professional', 'mechanic_essential', 'mechanic_pro', 'shop_pro', 'enterprise'],
  'maintenance_schedules': ['basic', 'professional', 'mechanic_essential', 'mechanic_pro', 'shop_pro', 'enterprise'],
  
  // Job Management - Allow all tiers, but with different capabilities
  'job_management': ['free', 'basic', 'professional', 'mechanic_essential', 'mechanic_pro', 'shop_pro', 'enterprise'],
  'ai_diagnostics': ['professional', 'mechanic_pro', 'shop_pro', 'enterprise'], // No free tier
  'ai_estimate': ['professional', 'mechanic_pro', 'shop_pro', 'enterprise'], // No free tier
  'quote_generation': ['professional', 'mechanic_pro', 'shop_pro', 'enterprise'],
  
  // Advanced Features
  'premium_vin_decode': ['professional', 'mechanic_pro', 'shop_pro', 'enterprise'],
  'ocr_scanning': ['professional', 'mechanic_pro', 'shop_pro', 'enterprise'],
  'market_values': ['free', 'professional', 'mechanic_pro', 'shop_pro', 'enterprise'], // Free tier: 1x/month
  'document_upload': ['basic', 'professional', 'mechanic_essential', 'mechanic_pro', 'shop_pro', 'enterprise'], // No free tier
  'ev_specifications': ['basic', 'professional', 'mechanic_essential', 'mechanic_pro', 'shop_pro', 'enterprise'],
  
  // Admin Features - Only for actual admin users, not subscription-based
  'admin_dashboard': [], // Admin access is controlled by user_type, not subscription tier
  
  // Parts Search Limits
  'unlimited_parts_search': ['professional', 'mechanic_pro', 'shop_pro', 'enterprise']
};

export const useSubscription = () => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Fetch current subscription
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription
  } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: subscriptionAPI.getCurrentSubscription,
    enabled: isAuthenticated,
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });

  // Fetch usage stats
  const {
    data: usageStats,
    isLoading: usageLoading,
    refetch: refetchUsage
  } = useQuery({
    queryKey: ['subscription-usage'],
    queryFn: subscriptionAPI.getUsageStats,
    enabled: isAuthenticated,
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });

  // Check if user has access to a specific feature
  const hasFeatureAccess = useCallback((feature) => {
    // Admin users should have access to most features
    if (user?.user_type === 'admin') {
      // Admin users get access to everything except some premium features
      const adminRestrictedFeatures = ['premium_vin_decode', 'ocr_scanning', 'market_values'];
      if (adminRestrictedFeatures.includes(feature)) {
        return false; // Even admins need proper subscription for premium features
      }
      return true; // Admin users get access to most features
    }
    
    // Job Management is only for mechanics, dealers, and admins - not for car owners (consumers)
    if (feature === 'job_management') {
      if (user?.user_type !== 'mechanic' && user?.user_type !== 'dealer') {
        return false;
      }
    }
    
    // Use subscription data if available, otherwise fall back to user's subscription_tier
    const currentTier = subscription?.tier || user?.subscription_tier || 'free';
    
    if (!currentTier) {
      // Default to free tier access if no tier data is available
      const allowedTiers = FEATURE_ACCESS[feature];
      if (!allowedTiers) return false;
      return allowedTiers.includes('free');
    }
    
    const allowedTiers = FEATURE_ACCESS[feature];
    if (!allowedTiers) return false;
    
    return allowedTiers.includes(currentTier);
  }, [subscription, user]);

  // Check if user's tier is at least the required tier
  const hasTierAccess = useCallback((requiredTier) => {
    // Admin users should have access to most tiers
    if (user?.user_type === 'admin') {
      return true; // Admin users get access to all tiers
    }
    
    // Use subscription data if available, otherwise fall back to user's subscription_tier
    const currentTier = subscription?.tier || user?.subscription_tier || 'free';
    
    if (!currentTier) {
      // Default to free tier if no tier data is available
      const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 0;
      return requiredTierLevel === 0; // Only allow free tier features
    }
    
    const userTierLevel = TIER_HIERARCHY[currentTier] || 0;
    const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 0;
    
    return userTierLevel >= requiredTierLevel;
  }, [subscription, user]);

  // Check if user can perform a search
  const canSearch = useCallback(() => {
    if (!subscription) {
      // Default to allowing searches if subscription data is not available
      return true;
    }
    
    // Unlimited searches
    if (subscription.search_limit === -1) return true;
    
    // Check remaining searches
    return subscription.searches_remaining > 0;
  }, [subscription]);

  // Get subscription tier display name
  const getTierDisplayName = useCallback((tier = subscription?.tier || user?.subscription_tier || 'free') => {
    logger.log('🔍 getTierDisplayName called with:', { tier, subscription: subscription?.tier, userTier: user?.subscription_tier });
    
    // Admin users should show a special display name
    if (user?.user_type === 'admin') {
      return 'System Admin';
    }
    
    const displayNames = {
      'free': 'Basic',
      'basic': 'Basic',
      'professional': 'Professional',   // car-owner tier -- NOT a mechanic tier
      'essential': 'Mechanic Pro',      // legacy spelling of mechanic_essential
      'pro': 'Professional',
      'mechanic_pro': 'Mechanic Premium',
      'mechanic_essential': 'Mechanic Pro',
      'shop_pro': 'Shop Pro', // Legacy
      'enterprise': 'Enterprise' // Legacy
    };
    
    const result = displayNames[tier] || 'Free Trial';
    logger.log('📋 getTierDisplayName result:', result);
    return result;
  }, [subscription, user]);

  // Get upgrade recommendations
  const getUpgradeRecommendations = useCallback(() => {
    // Admin users don't need upgrade recommendations
    if (user?.user_type === 'admin') {
      return [];
    }
    
    const currentTier = subscription?.tier || user?.subscription_tier || 'free';
    if (!currentTier) return [];
    
    const recommendations = [];
    
    // Check if user is on free tier
    if (currentTier === 'free') {
      recommendations.push({
        tier: 'basic',
        reason: 'Get vehicle profile management and basic maintenance tracking',
        price: '$1.99/month'
      });
    }
    
    // Check if user needs unlimited searches
    if (subscription && subscription.search_limit !== -1 && subscription.searches_remaining < 5) {
      recommendations.push({
        tier: 'professional',
        reason: 'Document Upload and advanced features',
        price: '$29.99/month'
      });
    }
    
    // Check if user is on basic tier and might want professional features
    if (currentTier === 'basic') {
      recommendations.push({
        tier: 'professional',
        reason: 'Unlimited, AI diagnostics, and advanced tools',
        price: '$29.99/month'
      });
    }
    
    return recommendations;
  }, [subscription, user]);

  // Refresh subscription data
  const refreshSubscription = useCallback(async () => {
    await Promise.all([
      refetchSubscription(),
      refetchUsage()
    ]);
    
    // Invalidate related queries
    queryClient.invalidateQueries({ queryKey: ['subscription-current'] });
    queryClient.invalidateQueries({ queryKey: ['subscription-usage'] });
  }, [refetchSubscription, refetchUsage, queryClient]);

  return {
    // Data
    subscription,
    usageStats,
    
    // Loading states
    subscriptionLoading,
    usageLoading,
    
    // Error state
    subscriptionError,
    
    // Utility functions
    hasFeatureAccess,
    hasTierAccess,
    canSearch,
    getTierDisplayName,
    getUpgradeRecommendations,
    refreshSubscription,
    
    // Computed values
    isAdmin: user?.user_type === 'admin',
    isFreeTier: user?.user_type === 'admin' ? false : (subscription?.tier || user?.subscription_tier || 'free') === 'free',
    // 'mechanic_essential'/'mechanic_pro' are the canonical stored values (the
    // SubscriptionTier enum and the subscriptions_tier_check constraint).
    // 'essential'/'pro' are legacy strings a since-fixed checkout path wrote,
    // still present on existing rows — see PR for the migration.
    isBasicTier: user?.user_type === 'admin' ? false : ['basic', 'essential', 'mechanic_essential'].includes(subscription?.tier || user?.subscription_tier || 'free'),
    isProfessionalTier: user?.user_type === 'admin' ? false : ['professional', 'pro', 'mechanic_pro'].includes(subscription?.tier || user?.subscription_tier || 'free'),
    isEnterpriseTier: user?.user_type === 'admin' ? false : (subscription?.tier || user?.subscription_tier || 'free') === 'enterprise',
    
    // Quick access checks
    canAccessVehicleProfiles: hasFeatureAccess('vehicle_profiles'),
    canAccessJobManagement: hasFeatureAccess('job_management'), // User type check is done in hasFeatureAccess
    canAccessAIDiagnostics: hasFeatureAccess('ai_diagnostics'),
    canAccessAdmin: user?.user_type === 'admin' || user?.subscription_tier === 'enterprise' || user?.user_type === 'professional',
    hasUnlimitedSearches: hasFeatureAccess('unlimited_parts_search')
  };
};
